import {
	Button,
	Link as ChakraLink,
	ChakraProps,
	useStyleConfig,
} from "@chakra-ui/react";
import { InlineLinkButtonFragment } from "src/__generated__/cms-schema.codegen";
import { NextLink } from "src/components/NextLink";
import { createBreadcrumbs } from "src/lib/utils";
import { CMSButtonLabel } from "./CMSButtonLabel";
import siteConfig from "../../../funk-experts.config.json";

export type CMSLinkButtonStyle = "outline" | "solid" | "text";

export type CMSLinkButtonProps = InlineLinkButtonFragment & {
	inHeader?: boolean;
	children?: React.ReactNode;
} & ChakraProps;

export const useGetInternalHref = (
	internal: InlineLinkButtonFragment["internal"],
) => {
	switch (internal?.__typename) {
		case "BreadcrumbRecord": {
			return `/${createBreadcrumbs(internal)
				.map(({ slug }) => slug)
				.join("/")}`;
		}

		case "PageRecord": {
			return `/${internal.slug === "index" ? "" : internal.slug}`;
		}

		default:
			return "#";
	}
};

type WrapperCommonProps = Omit<
	CMSLinkButtonProps,
	| "label"
	| "linkType"
	| "linkStyle"
	| "jumpMark"
	| "internal"
	| "url"
	| "leftIcon"
	| "rightIcon"
	| "analyticsId"
	| "inHeader"
	| "children"
	| "title"
	| "__typename"
	| "text"
	| "__css"
>;

type WrapperProps = Pick<
	CMSLinkButtonProps,
	"inHeader" | "title" | "analyticsId"
> & {
	internalHref: string;
	common: WrapperCommonProps;
	children: React.ReactNode;
};

const InternalWrapper: React.FC<WrapperProps> = ({
	inHeader,
	title,
	analyticsId,
	internalHref,
	common,
	children,
}) => {
	return inHeader ? (
		<NextLink href={internalHref} passHref>
			<Button
				as="a"
				title={title ?? undefined}
				{...common}
				id={analyticsId}
			>
				{children}
			</Button>
		</NextLink>
	) : (
		<NextLink href={internalHref} passHref>
			<ChakraLink {...common} id={analyticsId} title={title ?? undefined}>
				{children}
			</ChakraLink>
		</NextLink>
	);
};

export const CMSLinkButton: React.FC<CMSLinkButtonProps> = ({
	label,
	linkType,
	linkStyle,
	jumpMark,
	internal,
	url,
	leftIcon,
	rightIcon,
	analyticsId,
	inHeader,
	children,
	title,
	// destructure the following three props to get rid of it
	__typename,
	text,
	__css,
	...rest
}) => {
	const sx = useStyleConfig("CMSButton", {
		variant: linkStyle,
		inHeader,
	});

	const internalHref = useGetInternalHref(internal);

	const common = {
		variant: linkStyle,
		sx,
		"data-testid": "cms-link-button",
		...(url?.endsWith(".pdf") ? { download: true } : {}),
		...rest,
	};

	switch (linkType) {
		case "external": {
			// We may link to funk-experts.com from a funk-experts.de page as an "external" link.
			// In these cases, we don't want to show the external link icon so we make a check here.
			const isFEXUrl = siteConfig.brands.some((brand) =>
				url?.includes(brand.slug),
			);

			return (
				<ChakraLink
					{...common}
					href={url ?? "#"}
					id={analyticsId}
					isExternal={!isFEXUrl}
					title={title ?? undefined}
				>
					{children ? (
						children
					) : (
						<CMSButtonLabel
							leftIcon={leftIcon?.name}
							rightIcon={rightIcon?.name}
							label={label}
							isExternal={!isFEXUrl}
						/>
					)}
				</ChakraLink>
			);
		}
		case "internal": {
			if (!internal) {
				return null;
			}

			return (
				<InternalWrapper
					inHeader={inHeader}
					title={title}
					analyticsId={analyticsId}
					internalHref={internalHref}
					common={common}
				>
					{children ? (
						children
					) : (
						<CMSButtonLabel
							leftIcon={leftIcon?.name}
							rightIcon={rightIcon?.name}
							label={label}
						/>
					)}
				</InternalWrapper>
			);
		}
		default:
			return jumpMark ? (
				<ChakraLink
					{...common}
					href={jumpMark}
					id={analyticsId}
					title={title ?? undefined}
				>
					{children ? (
						children
					) : (
						<CMSButtonLabel
							leftIcon={leftIcon?.name}
							rightIcon={rightIcon?.name}
							label={label}
						/>
					)}
				</ChakraLink>
			) : null;
	}
};

// 🔬 RTL
